import { SubscriptionOptionEnum } from "@shared_packages/toolkit/types";
import { CompanySubscriptionHistoryEntry } from "../../../../util/types";
import {
  formatFeeScheduleSummary,
  formatMoneyFromPence,
} from "./subscription-billing.helpers";
import { subscriptionTabLabel } from "./subscriptionBillingTabs.config";

export type HistoryStatusTone = "live" | "warn" | "draft" | "ended" | "muted";

/** Human label + colour tone for a history row's status. */
export function historyStatus(entry: {
  status?: string | null;
  cancelAtPeriodEnd?: boolean | null;
}): { label: string; tone: HistoryStatusTone } {
  const status = (entry.status ?? "").toLowerCase();
  switch (status) {
    case "active":
      return entry.cancelAtPeriodEnd
        ? { label: "Cancel scheduled", tone: "warn" }
        : { label: "Active", tone: "live" };
    case "trialing":
      return entry.cancelAtPeriodEnd
        ? { label: "Cancel scheduled", tone: "warn" }
        : { label: "Trialing", tone: "live" };
    case "past_due":
    case "unpaid":
    case "payment_failed":
    case "payment_action_required":
      return { label: status.replace(/_/g, " "), tone: "warn" };
    case "paused":
      return { label: "Paused", tone: "warn" };
    case "incomplete":
      return { label: "Preconfigured", tone: "draft" };
    case "incomplete_expired":
      return { label: "Draft expired", tone: "muted" };
    case "deleted":
    case "deleted_in_process":
    case "canceled":
    case "cancelled":
      return { label: "Cancelled", tone: "ended" };
    case "processing":
      return { label: "Processing", tone: "muted" };
    default:
      return { label: status || "—", tone: "muted" };
  }
}

/** "Payments Only · BASIC · monthly" */
export function historyPlanLabel(entry: {
  subscriptionOption: SubscriptionOptionEnum | string;
  subscriptionLevel?: string | null;
  subscriptionInterval?: string | null;
}): string {
  const parts = [subscriptionTabLabel(String(entry.subscriptionOption))];
  if (entry.subscriptionLevel) parts.push(String(entry.subscriptionLevel));
  const interval = (entry.subscriptionInterval ?? "").toLowerCase();
  if (interval === "month") parts.push("monthly");
  else if (interval === "year") parts.push("annual");
  return parts.join(" · ");
}

/** One-line pricing summary; Payments products describe their fees, Toolkit its catalogue price. */
export function historyPricingLabel(
  entry: CompanySubscriptionHistoryEntry
): string {
  const option = String(entry.subscriptionOption);
  const isPayments =
    option === SubscriptionOptionEnum.PAYMENTS_ONLY ||
    option === SubscriptionOptionEnum.PAYMENTS_ONLY_EU;
  if (!isPayments) {
    return entry.priceProfileKey
      ? `Catalogue · ${entry.priceProfileKey}`
      : "Catalogue";
  }
  const mode = (entry.paymentsPricingMode ?? "").toLowerCase();
  if (mode === "bespoke" || entry.hasBespokePricing) return "Bespoke";
  if (mode === "monthly_fee") {
    const fee =
      entry.monthlyFeeMinor != null
        ? formatMoneyFromPence(entry.monthlyFeeMinor, entry.currency || "gbp")
        : "—";
    return `Monthly fee ${fee}`;
  }
  const schedule = formatFeeScheduleSummary(entry.feeSchedules);
  if (schedule !== "—") return schedule;
  return entry.hasHighRiskSubscriptionPricing
    ? "High-risk tiered"
    : "Not configured";
}

/** "20% off · 4 months" or null. */
export function historyDiscountLabel(
  entry: Pick<CompanySubscriptionHistoryEntry, "subscriptionDiscount">
): string | null {
  const discount = entry.subscriptionDiscount;
  if (!discount?.percentOff) return null;
  const duration =
    discount.duration === "repeating"
      ? `${discount.durationInMonths ?? "?"} months`
      : discount.duration ?? "once";
  return `${discount.percentOff}% off · ${duration}`;
}

/** Newest first; rows without a creation date go last. */
export function sortHistoryEntries<T extends { createdAt?: string | null }>(
  entries: T[]
): T[] {
  return [...entries].sort((a, b) => {
    const left = a.createdAt
      ? Date.parse(a.createdAt)
      : Number.NEGATIVE_INFINITY;
    const right = b.createdAt
      ? Date.parse(b.createdAt)
      : Number.NEGATIVE_INFINITY;
    return right - left;
  });
}
